/* eslint-disable react/prop-types */
import React from "react";
import { useModalMain } from "../../../../contexts/modal";
import EmptyValue from "../../../components/EmptyValue";
import { formatShortDate } from "../../../utils/format";

// Content for the "View" link on the Delivery Details column (./columns.jsx). Renders
// inside the app's existing generic modal shell (src/contexts/MainModal.jsx,
// ModalProvider already mounted above the router in src/index.js) -- no new modal
// system. That shell's rounded white card is already close to the prototype mockup this
// was designed against; only the content below is custom.
//
// Like every other component this app opens via openModal(), this one takes no props --
// it reads its data through useModalMain()'s `formValues`, the same contract
// CreateOrder/CreateUser/etc. already use. `formValues` here is
// `{ orderId, ...deliveryDetailsDTO }`, where the DTO shape comes from pillup-api's
// classes/Delivery/normalize.js (trackingId, shippingCompany, parcelWeight, pickupDate,
// deliveryDate, deliveryStatus, transitDays, trackingLink).
//
// No separate "Status Delivery" field (redundant with the status pill below) and no
// "Vendors" field (redundant with Shipping Company) -- trimmed relative to the
// prototype's own mockup, which showed both.
const STATUS_LABELS = {
    PACKED: "Packed",
    PICKED_UP: "Picked Up",
    IN_TRANSIT: "In Transit",
    OUT_FOR_DELIVERY: "Out For Delivery",
    DELIVERED: "Delivered",
    RTO: "Returned to Origin",
    CANCELLED: "Cancelled",
};

function Field({ label, value }) {
    return (
        <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">{label}</p>
            <p className="font-sans text-black">{value ?? <EmptyValue />}</p>
        </div>
    );
}

function DeliveryDetailsModal() {
    const { formValues } = useModalMain();
    const { orderId, trackingId, shippingCompany, parcelWeight, deliveryDate, deliveryStatus, transitDays } =
        formValues || {};

    return (
        <div className="flex flex-col gap-5 min-w-[320px]">
            <div>
                <h2 className="text-2xl font-bold font-sans">Delivery Details</h2>
                <p className="text-sm text-gray-500">
                    Order #{orderId}
                    {trackingId && <> &nbsp;·&nbsp; Tracking: {trackingId}</>}
                </p>
            </div>

            <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Status</p>
                {deliveryStatus ? (
                    <span className="inline-block rounded-full bg-elm-100 px-4 py-1.5 font-sans font-semibold text-elm-700">
                        {STATUS_LABELS[deliveryStatus] || deliveryStatus}
                    </span>
                ) : (
                    <EmptyValue />
                )}
            </div>

            <div className="grid grid-cols-2 gap-4">
                <Field label="Shipping Company" value={shippingCompany} />
                <Field label="Weight of the Parcel" value={parcelWeight != null ? `${parcelWeight} kg` : null} />
                <Field label="Transit Days" value={transitDays} />
                <Field label="Delivery Date" value={deliveryDate ? formatShortDate(deliveryDate) : null} />
            </div>
        </div>
    );
}

export default DeliveryDetailsModal;
